import { useEffect, useMemo } from 'react'
import { Logger } from '@/components/Logger'
import { useGetUserSelf } from '@/hooks/v1'
import { leverageGetAccruedInterestByUserQueryKey } from '@/hooks/v1/hooks/useLeverageGetAccruedInterestByUser'
import type { CurrentLeverageAccruedInterest } from '@/hooks/v1/types/CurrentLeverageAccruedInterest'
import type {
  LeverageGetAccruedInterestByUserQuery,
  LeverageGetAccruedInterestByUserStatus200,
} from '@/hooks/v1/types/LeverageGetAccruedInterestByUser'
import { queryClient } from '@/services/api'
import { subscribeRealtime } from '@/services/api/realtimeSubscription'
import { isWebSocketErrorMessage } from './useWebSocketStatus'

type UseLeverageAccruedInterestSubscriptionOptions = {
  enabled?: boolean
}

type StreamEntry<T> = {
  Val?: T
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object')

const hasValWrapper = (value: unknown): value is StreamEntry<unknown> =>
  isRecord(value) && 'Val' in value

const isAccruedInterestUpdate = (value: unknown): value is CurrentLeverageAccruedInterest =>
  isRecord(value) &&
  typeof value.asset_id === 'string' &&
  typeof value.position_id === 'string' &&
  typeof value.current_accrued_interest_usd === 'string'

const extractAccruedInterestUpdates = (message: unknown): CurrentLeverageAccruedInterest[] => {
  if (Array.isArray(message)) {
    return message.flatMap(extractAccruedInterestUpdates)
  }

  if (!isRecord(message)) {
    return []
  }

  if (hasValWrapper(message)) {
    return extractAccruedInterestUpdates(message.Val)
  }

  if (isAccruedInterestUpdate(message)) {
    return [message]
  }

  if (Array.isArray(message.current_leverage_accrued_interest)) {
    return message.current_leverage_accrued_interest.filter(isAccruedInterestUpdate)
  }

  if (Array.isArray(message.data)) {
    return message.data.filter(isAccruedInterestUpdate)
  }

  if ('data' in message) {
    return extractAccruedInterestUpdates(message.data)
  }

  if (Array.isArray(message.entries)) {
    return message.entries.flatMap(extractAccruedInterestUpdates)
  }

  return []
}

const getAccruedInterestKey = (item: CurrentLeverageAccruedInterest) =>
  `${item.asset_id}:${item.position_id}`

const hasAccruedInterestDiff = (
  previous: CurrentLeverageAccruedInterest | undefined,
  update: CurrentLeverageAccruedInterest,
) => {
  if (!previous) {
    return true
  }

  return Object.entries(update as Record<string, unknown>).some(
    ([key, value]) => (previous as Record<string, unknown>)[key] !== value,
  )
}

const mergeAccruedInterestUpdates = (
  existing: CurrentLeverageAccruedInterest[],
  updates: CurrentLeverageAccruedInterest[],
) => {
  if (updates.length === 0) {
    return existing
  }

  const mergedByKey = new Map<string, CurrentLeverageAccruedInterest>()
  const existingOrder: string[] = []

  existing.forEach((item) => {
    const key = getAccruedInterestKey(item)
    mergedByKey.set(key, item)
    existingOrder.push(key)
  })

  const newKeys: string[] = []
  let changed = false

  updates.forEach((update) => {
    const key = getAccruedInterestKey(update)
    const previous = mergedByKey.get(key)

    if (previous) {
      if (hasAccruedInterestDiff(previous, update)) {
        mergedByKey.set(key, { ...previous, ...update })
        changed = true
      }
      return
    }

    mergedByKey.set(key, update)
    newKeys.push(key)
    changed = true
  })

  if (!changed) {
    return existing
  }

  return [...existingOrder, ...newKeys]
    .map((key) => mergedByKey.get(key))
    .filter(Boolean) as CurrentLeverageAccruedInterest[]
}

const getExistingAccruedInterestItems = (data: unknown): CurrentLeverageAccruedInterest[] => {
  if (Array.isArray(data)) {
    return data.filter(isAccruedInterestUpdate)
  }

  if (!isRecord(data)) {
    return []
  }

  if (Array.isArray(data.current_leverage_accrued_interest)) {
    return data.current_leverage_accrued_interest.filter(isAccruedInterestUpdate)
  }

  if (isAccruedInterestUpdate(data)) {
    return [data]
  }

  return []
}

const getQueryParamsFromKey = (
  queryKey: unknown,
): LeverageGetAccruedInterestByUserQuery | undefined => {
  if (!Array.isArray(queryKey) || queryKey.length < 2 || !isRecord(queryKey[1])) {
    return undefined
  }

  return queryKey[1] as LeverageGetAccruedInterestByUserQuery
}

const matchesQueryParams = (
  update: CurrentLeverageAccruedInterest,
  params?: LeverageGetAccruedInterestByUserQuery,
) => {
  if (params?.asset_id && update.asset_id !== params.asset_id) {
    return false
  }

  if (params?.position_id && update.position_id !== params.position_id) {
    return false
  }

  return true
}

const isSingleAccruedInterestQuery = (
  data: unknown,
  params?: LeverageGetAccruedInterestByUserQuery,
) => isAccruedInterestUpdate(data) || Boolean(params?.asset_id && params?.position_id)

const updateAccruedInterestQueryData = (
  previous: LeverageGetAccruedInterestByUserStatus200 | undefined,
  updates: CurrentLeverageAccruedInterest[],
  params?: LeverageGetAccruedInterestByUserQuery,
): LeverageGetAccruedInterestByUserStatus200 | undefined => {
  if (updates.length === 0) {
    return previous
  }

  const previousData = previous?.data

  if (isSingleAccruedInterestQuery(previousData, params)) {
    const update = updates[0]
    const current = isAccruedInterestUpdate(previousData) ? previousData : undefined

    if (!hasAccruedInterestDiff(current, update)) {
      return previous
    }

    return {
      ...(previous ?? {}),
      data: {
        ...(current ?? {}),
        ...update,
      },
    } as LeverageGetAccruedInterestByUserStatus200
  }

  const currentItems = getExistingAccruedInterestItems(previousData)
  const nextItems = mergeAccruedInterestUpdates(currentItems, updates)

  if (nextItems === currentItems) {
    return previous
  }

  const previousDataRecord = isRecord(previousData)
    ? (previousData as Record<string, unknown>)
    : undefined

  if (previousDataRecord && Array.isArray(previousDataRecord.current_leverage_accrued_interest)) {
    return {
      ...(previous ?? {}),
      data: {
        ...previousDataRecord,
        current_leverage_accrued_interest: nextItems,
      },
    } as LeverageGetAccruedInterestByUserStatus200
  }

  return {
    ...(previous ?? {}),
    data: nextItems,
  } as LeverageGetAccruedInterestByUserStatus200
}

const updateAccruedInterestQueries = (
  userId: string,
  updates: CurrentLeverageAccruedInterest[],
) => {
  const userUpdates = updates.filter((update) => !update.user_id || update.user_id === userId)

  if (userUpdates.length === 0) {
    return
  }

  const baseQueryKey = leverageGetAccruedInterestByUserQueryKey({ path: { user_id: userId } })
  const cachedQueries = queryClient.getQueriesData({ queryKey: baseQueryKey })
  const queryKeys = cachedQueries.length
    ? cachedQueries.map(([queryKey]) => queryKey)
    : [leverageGetAccruedInterestByUserQueryKey({ path: { user_id: userId }, query: {} })]

  queryKeys.forEach((queryKey) => {
    const params = getQueryParamsFromKey(queryKey)
    const matchingUpdates = userUpdates.filter((update) => matchesQueryParams(update, params))

    if (matchingUpdates.length === 0) {
      return
    }

    queryClient.setQueryData(queryKey, (previous: LeverageGetAccruedInterestByUserStatus200) =>
      updateAccruedInterestQueryData(previous, matchingUpdates, params),
    )
  })
}

const clearAccruedInterestQueries = (userId: string) => {
  const baseQueryKey = leverageGetAccruedInterestByUserQueryKey({ path: { user_id: userId } })
  const cachedQueries = queryClient.getQueriesData({ queryKey: baseQueryKey })

  cachedQueries.forEach(([queryKey]) => {
    queryClient.setQueryData(queryKey, (previous: LeverageGetAccruedInterestByUserStatus200) => {
      const previousData = previous?.data
      if (Array.isArray(previousData)) return { ...(previous ?? {}), data: [] }
      const previousRecord = isRecord(previousData)
        ? (previousData as Record<string, unknown>)
        : undefined
      if (previousRecord && Array.isArray(previousRecord.current_leverage_accrued_interest)) {
        return {
          ...(previous ?? {}),
          data: { ...previousRecord, current_leverage_accrued_interest: [] },
        }
      }
      return previous
    })
  })
}

export const useLeverageAccruedInterestSubscription = ({
  enabled = true,
}: UseLeverageAccruedInterestSubscriptionOptions = {}) => {
  const { data: userSelfData } = useGetUserSelf()
  const userId = useMemo(() => userSelfData?.data?.id ?? null, [userSelfData])

  useEffect(() => {
    if (!enabled || !userId) {
      return
    }

    const unsubscribe = subscribeRealtime(
      {
        onMessage: (data) => {
          try {
            if (isWebSocketErrorMessage(data)) {
              Logger.error(
                'DEBUG WS LEVERAGE ACCRUED INTEREST: Received error message, skipping setQueryData:',
                data,
              )
              return
            }

            if (
              !data ||
              (typeof data === 'object' && Object.keys(data).length === 0) ||
              data === ''
            ) {
              return
            }

            const clearedUserIds =
              isRecord(data) && Array.isArray(data.cleared_user_ids)
                ? data.cleared_user_ids.filter(
                    (value): value is string => typeof value === 'string',
                  )
                : []
            const wasCleared = clearedUserIds.includes(userId)
            if (wasCleared) clearAccruedInterestQueries(userId)

            const updates = extractAccruedInterestUpdates(data)

            if (!updates.length) {
              return
            }

            updateAccruedInterestQueries(userId, updates)
          } catch (error) {
            Logger.error(
              'DEBUG WS LEVERAGE ACCRUED INTEREST: Error parsing WebSocket message:',
              error,
            )
          }
        },
        onOpen: () => {
          Logger.log(
            'DEBUG WS LEVERAGE ACCRUED INTEREST: WebSocket connected successfully to user leverage accrued interest stream',
          )
        },
        onError: (error) => {
          Logger.error('DEBUG WS LEVERAGE ACCRUED INTEREST: WebSocket error:', error)
        },
        onClose: (event) => {
          Logger.log(
            'DEBUG WS LEVERAGE ACCRUED INTEREST: WebSocket connection closed, code:',
            event.code,
            'wasClean:',
            event.wasClean,
          )
        },
      },
      {
        key: `leverage-accrued-interest:${userId}`,
        path: '/v1/user/leverage/accrued_interest/stream',
        subscribeData: { subscribe: [userId] },
        unsubscribeData: { unsubscribe: [userId] },
      },
    )

    return () => {
      unsubscribe()
    }
  }, [enabled, userId])
}
